import { useState, useEffect } from 'react'
import { X, Gift, Sparkles, PartyPopper } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/supabase/client'
import { getActiveReferralEvent } from '@/supabase/referral/repository/referralEventRepository'
import { refereeRewardLabel, type RefereeRewardLabel, type ReferralEventState } from '@/lib/referralEvent'

type RefereeRow = { event_id: number | null; is_event_period: boolean; first_payment_at: string | null }

const STORAGE_KEY = 'referral_welcome_hidden_until'
const DAYS_TO_HIDE = 1 // 닫으면 1일 후 다시 표시

export default function ReferralWelcomeModal() {
  const navigate = useNavigate()
  const location = useLocation()
  const { showReferralWelcomeModal, setShowReferralWelcomeModal } = useLoginModal()
  const { user } = useAuth()
  const [referralEvent, setReferralEvent] = useState<ReferralEventState>(undefined)
  const [refereeRow, setRefereeRow] = useState<RefereeRow | null>(null)
  const [isRefereeBeforeFirstPayment, setIsRefereeBeforeFirstPayment] = useState(false)
  const [shouldShow, setShouldShow] = useState(false)

  // 피추천인 + 첫 결제 전인지 체크
  useEffect(() => {
    const checkRefereeStatus = async () => {
      if (!user) {
        setIsRefereeBeforeFirstPayment(false)
        return
      }

      const { data, error } = await supabase
        .from('referral')
        .select('first_payment_at, event_id, is_event_period')
        .eq('referee_id', user.id)
        .single()

      if (error || !data) {
        setRefereeRow(null)
        setIsRefereeBeforeFirstPayment(false)
        return
      }

      setRefereeRow(data)
      setIsRefereeBeforeFirstPayment(data.first_payment_at === null)
    }
    checkRefereeStatus()
  }, [user?.id, showReferralWelcomeModal]) // 가입 직후 context 오픈 시 방금 생성된 referral 행을 다시 읽음

  // 이벤트 조회 (실패 시 undefined 유지 = 혜택 줄 미노출)
  useEffect(() => {
    getActiveReferralEvent()
      .then(setReferralEvent)
      .catch((error) => console.error('[ReferralWelcomeModal] 이벤트 조회 오류:', error))
  }, [])

  // referral 행 확인 전에는 혜택 줄 미표시 (현재 이벤트로 추정하지 않음)
  const rewardLabel: RefereeRewardLabel = refereeRow
    ? refereeRewardLabel(refereeRow, referralEvent)
    : { kind: 'hidden' }

  // 표시 여부 결정
  useEffect(() => {
    // context에서 직접 열기 (회원가입 직후)
    if (showReferralWelcomeModal) {
      setShouldShow(true)
      return
    }

    // 피추천인 + 첫 결제 전이면 자동 표시
    if (isRefereeBeforeFirstPayment) {
      // 마이페이지나 초대장 페이지에서는 안 뜸
      if (location.pathname === '/mypage' || location.pathname === '/invite') {
        setShouldShow(false)
        return
      }

      // 숨김 기간 체크
      const hiddenUntil = localStorage.getItem(STORAGE_KEY)
      if (hiddenUntil) {
        const hiddenDate = new Date(hiddenUntil)
        if (new Date() < hiddenDate) {
          setShouldShow(false)
          return
        }
      }

      setShouldShow(true)
    } else {
      setShouldShow(false)
    }
  }, [showReferralWelcomeModal, isRefereeBeforeFirstPayment, location.pathname])

  const handleGoToMyPage = () => {
    setShowReferralWelcomeModal(false)
    setShouldShow(false)
    navigate('/mypage')
  }

  const handleClose = () => {
    setShowReferralWelcomeModal(false)
    setShouldShow(false)
    // 피추천인 자동 표시였다면 1일 후 다시 표시
    if (isRefereeBeforeFirstPayment) {
      const hideUntil = new Date()
      hideUntil.setDate(hideUntil.getDate() + DAYS_TO_HIDE)
      localStorage.setItem(STORAGE_KEY, hideUntil.toISOString())
    }
  }

  if (!shouldShow) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 z-[10000]">
        {/* 닫기 버튼 - 왼쪽 위 */}
        <button
          onClick={handleClose}
          className="absolute top-4 left-4 p-2 rounded-full hover:bg-white/50 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* 헤더 - 아이콘 & 이펙트 */}
        <div className="relative pt-8 pb-4 text-center overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <Sparkles className="w-32 h-32 text-purple-500 animate-pulse" />
          </div>
          
          {/* 메인 아이콘 */}
          <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full shadow-lg mb-4">
            <Gift className="w-10 h-10 text-white" />
            <div className="absolute -top-2 -right-2">
              <PartyPopper className="w-8 h-8 text-yellow-400 animate-bounce" />
            </div>
          </div>

          {/* 제목 */}
          <h2 className="text-2xl font-bold mb-2">
            <span className="text-2xl">🎉</span>{' '}
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">가입을 환영합니다!</span>
          </h2>
          <p className="text-gray-600 px-6">
            친구의 초대로 오셨군요!
          </p>
        </div>

        {/* 본문 */}
        <div className="px-6 pb-6 space-y-4">
          {/* 혜택 안내 카드 */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border-2 border-green-200">
            <div className="text-center space-y-3">
              <p className="text-4xl font-bold text-green-600">90% 할인</p>
              <p className="text-lg font-semibold text-gray-900">
                첫 달 구독료가 90% 할인됩니다!
              </p>
              <div className="text-sm text-gray-600 space-y-1">
                <p>스탠다드: <span className="line-through text-gray-400">₩9,900</span> → <span className="font-bold text-green-600">₩990</span></p>
                <p>프리미엄: <span className="line-through text-gray-400">₩24,900</span> → <span className="font-bold text-green-600">₩2,490</span></p>
              </div>
              {rewardLabel.kind !== 'hidden' && (
                <p className="text-xs text-purple-600 font-semibold pt-2">
                  {rewardLabel.kind === 'event'
                    ? `🔥 이벤트 기간! 지금 결제 시 ${rewardLabel.months}개월 추가 혜택${rewardLabel.maxReferees ? ` (선착순 ${rewardLabel.maxReferees}명 한정)` : ''}`
                    : rewardLabel.kind === 'confirm'
                      ? '+ 추가 혜택은 첫 결제 후 승인 시 확정됩니다'
                      : '+ 첫 결제 시 1개월 추가 혜택'}
                </p>
              )}
            </div>
          </div>

          {/* 버튼 */}
          <Button
            onClick={handleGoToMyPage}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-lg"
          >
            혜택 보기 →
          </Button>
        </div>
      </div>
    </div>
  )
}
